import React, { useState } from 'react';
import { MapPin, X, Flame } from 'lucide-react';

const ZONES = 3; // 3×3 grid → 9 zones
const ZONE_LABELS = ['Top-Left', 'Top-Center', 'Top-Right', 'Mid-Left', 'Center', 'Mid-Right', 'Bottom-Left', 'Bottom-Center', 'Bottom-Right'];

const zoneOf = (d) => {
  const cx = Math.min(ZONES - 1, Math.floor(d.equirect.uNorm * ZONES));
  const cy = Math.min(ZONES - 1, Math.floor(d.equirect.vNorm * ZONES));
  return cy * ZONES + cx;
};

/**
 * Overlays de-duplicated aerial detections as pins on the stitched site map with:
 *  - a 3×3 per-zone count breakdown (tap a zone to filter pins + list its objects),
 *  - a toggleable red damage-heat layer where damaged objects cluster.
 */
const AerialMapOverlay = ({ mapUrl, detections = [] }) => {
  const [sel, setSel] = useState(null);
  const [zone, setZone] = useState(null); // selected zone index or null
  const [heatOn, setHeatOn] = useState(true);

  const pins = detections.filter((d) => d.equirect && typeof d.equirect.uNorm === 'number');
  const damaged = pins.filter((d) => d.quality && d.quality.status === 'damaged');

  const zoneCounts = Array(ZONES * ZONES).fill(0);
  const zoneDamaged = Array(ZONES * ZONES).fill(0);
  pins.forEach((d) => {
    const z = zoneOf(d);
    zoneCounts[z] += 1;
    if (d.quality && d.quality.status === 'damaged') zoneDamaged[z] += 1;
  });
  const maxZone = Math.max(1, ...zoneCounts);

  const visiblePins = zone === null ? pins : pins.filter((d) => zoneOf(d) === zone);
  const zoneObjects = zone === null ? [] : pins.filter((d) => zoneOf(d) === zone);

  return (
    <div data-testid="aerial-map-overlay">
      <div className="flex items-center justify-end mb-2">
        {damaged.length > 0 && (
          <button
            onClick={() => setHeatOn((v) => !v)}
            className={`flex items-center gap-1 text-[11px] px-2 py-1 rounded border ${heatOn ? 'bg-red-50 border-red-300 text-red-600' : 'bg-white border-slate-200 text-slate-500'}`}
            data-testid="aerial-heat-toggle"
          >
            <Flame className="w-3 h-3" /> Damage heat ({damaged.length})
          </button>
        )}
      </div>

      <div className="relative rounded-lg overflow-hidden border border-slate-200 bg-slate-900">
        <img src={mapUrl} alt="Stitched aerial site map" className="w-full object-contain max-h-[420px]" data-testid="aerial-map-image" />

        {/* Damage heat blobs (behind pins) */}
        {heatOn && damaged.map((d, i) => (
          <div
            key={`heat-${d.id || i}`}
            className="absolute -translate-x-1/2 -translate-y-1/2 pointer-events-none z-0"
            style={{
              left: `${d.equirect.uNorm * 100}%`,
              top: `${d.equirect.vNorm * 100}%`,
              width: '22%', height: '22%',
              background: 'radial-gradient(circle, rgba(239,68,68,0.55) 0%, rgba(239,68,68,0) 70%)',
            }}
            data-testid="aerial-heat-blob"
          />
        ))}

        {visiblePins.map((d, idx) => {
          const isDamaged = d.quality && d.quality.status === 'damaged';
          return (
            <button
              key={d.id || idx}
              onClick={() => setSel(sel === (d.id || idx) ? null : (d.id || idx))}
              style={{ left: `${d.equirect.uNorm * 100}%`, top: `${d.equirect.vNorm * 100}%` }}
              className="absolute -translate-x-1/2 -translate-y-1/2 z-10"
              data-testid="aerial-pin"
            >
              <span className={`flex items-center justify-center w-4 h-4 rounded-full border-2 border-white shadow ${isDamaged ? 'bg-red-500' : 'bg-sky-500'} hover:scale-125 transition-transform`}>
                <MapPin className="w-2.5 h-2.5 text-white" />
              </span>
              {sel === (d.id || idx) && (
                <div className="absolute top-5 left-1/2 -translate-x-1/2 w-40 bg-white rounded-lg shadow-xl border border-slate-200 p-2 text-left z-20" onClick={(ev) => ev.stopPropagation()} data-testid="aerial-pin-popover">
                  <div className="flex justify-between items-start">
                    <p className="text-xs font-semibold text-slate-800">{d.id || `Object ${idx + 1}`}</p>
                    <span onClick={() => setSel(null)} className="text-slate-400 hover:text-slate-600 cursor-pointer"><X className="w-3 h-3" /></span>
                  </div>
                  {d.cluster && <p className="text-[11px] text-slate-500">Cluster {d.cluster}</p>}
                  {isDamaged && <p className="text-[11px] font-medium text-red-600">Damaged</p>}
                </div>
              )}
            </button>
          );
        })}
      </div>

      <div className="mt-3" data-testid="aerial-zone-breakdown">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold text-slate-600">
            Per-zone object count ({pins.length} located){zone !== null ? ` · showing ${ZONE_LABELS[zone]}` : ''}
          </p>
          {zone !== null && (
            <button onClick={() => setZone(null)} className="text-[11px] text-sky-600 hover:underline" data-testid="aerial-zone-clear">Show all</button>
          )}
        </div>
        <div className="grid grid-cols-3 gap-1">
          {zoneCounts.map((c, i) => (
            <button
              key={i}
              onClick={() => setZone(zone === i ? null : i)}
              className={`rounded p-2 text-center border transition-all ${zone === i ? 'border-sky-500 ring-1 ring-sky-400' : 'border-slate-200'}`}
              style={{ backgroundColor: `rgba(2,132,199,${0.08 + 0.5 * (c / maxZone)})` }}
              data-testid={`aerial-zone-${i}`}
              title={ZONE_LABELS[i]}
            >
              <div className="text-sm font-bold text-slate-800">{c}</div>
              <div className="text-[10px] text-slate-500">{ZONE_LABELS[i]}</div>
              {zoneDamaged[i] > 0 && <div className="text-[10px] font-medium text-red-600">{zoneDamaged[i]} damaged</div>}
            </button>
          ))}
        </div>

        {zone !== null && (
          <div className="mt-3 border border-slate-200 rounded-lg p-3" data-testid="aerial-zone-objects">
            <p className="text-xs font-semibold text-slate-700 mb-2">{ZONE_LABELS[zone]} — {zoneObjects.length} object(s)</p>
            {zoneObjects.length === 0 ? (
              <p className="text-xs text-slate-400">No objects in this zone.</p>
            ) : (
              <ul className="space-y-1 max-h-40 overflow-y-auto">
                {zoneObjects.map((d, i) => {
                  const isDamaged = d.quality && d.quality.status === 'damaged';
                  return (
                    <li key={d.id || i} className="flex items-center justify-between text-xs" data-testid="aerial-zone-object-row">
                      <span className="text-slate-700">{d.id || `Object ${i + 1}`}{d.cluster ? ` · Cluster ${d.cluster}` : ''}</span>
                      <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${isDamaged ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                        {isDamaged ? 'Damaged' : 'OK'}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AerialMapOverlay;
